import PhysicsEngine from '@magic-spells/physics-engine';
import FrameEngine from '@magic-spells/frame-engine';
import EventEmitter from './event-emitter.js';
import { normalizeColor, parseColor } from './color.js';
import { blobBaseStyle } from './blob-style.js';
import { resolveBlobContainer } from './blob-container.js';
import { parseShadow, lerpShadow } from './shadow.js';

// Spring travel distance recommended by physics-engine. All choreography is driven
// off p = position / TRAVEL — never the change event's own `progress` field, which
// is relative to each animateTo()'s endpoints and resets on a mid-flight retarget.
const TRAVEL = 1000;

// Finalize a morph once motion is sub-pixel for two consecutive frames instead of
// waiting for physics-engine's own 'complete' (|pos−end| < 1e-2 AND |vel| < 1e-2),
// which trails ~0.5s of visually dead settle. Units are TRAVEL (1000): a position
// within 1 of the end is sub-pixel for realistic on-screen travels, and a per-frame
// delta under 0.5 means the spring is no longer visibly moving.
const SETTLE_POSITION_EPSILON = 1;
const SETTLE_DELTA_EPSILON = 0.5;

// Longhands only — frame-engine snaps multi-value shorthands (border, borderRadius,
// boxShadow) discretely instead of interpolating them.
const DEFAULT_STYLE_PROPERTIES = [
	'backgroundColor',
	'borderTopLeftRadius',
	'borderTopRightRadius',
	'borderBottomRightRadius',
	'borderBottomLeftRadius',
	'borderTopWidth',
	'borderRightWidth',
	'borderBottomWidth',
	'borderLeftWidth',
	'borderTopColor',
	'borderRightColor',
	'borderBottomColor',
	'borderLeftColor',
];

const BORDER_SIDES = ['Top', 'Right', 'Bottom', 'Left'];

// frame-engine extrapolates past the end keyframes during spring overshoot and has
// no clamp for these — a negative value is invalid CSS and would stall a frame.
const CLAMP_POSITIVE = [
	'width',
	'height',
	'borderTopLeftRadius',
	'borderTopRightRadius',
	'borderBottomRightRadius',
	'borderBottomLeftRadius',
	'borderTopWidth',
	'borderRightWidth',
	'borderBottomWidth',
	'borderLeftWidth',
];

// Every inline style the engine may write on the real elements — captured once per
// show → hide lifecycle and restored when the morph fully unwinds.
const MANAGED_PROPERTIES = [
	'visibility',
	'display',
	'opacity',
	'transform',
	'transformOrigin',
	'willChange',
	'transition',
];

let scrollLockCount = 0;
let savedBodyOverflow = '';

/** Acquires one share of the module-level body scroll lock. */
function acquireScrollLock() {
	if (scrollLockCount++ === 0) {
		savedBodyOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
	}
}

/** Releases one share of the module-level body scroll lock. */
function releaseScrollLock() {
	if (scrollLockCount === 0) return;
	if (--scrollLockCount === 0) {
		document.body.style.overflow = savedBodyOverflow;
		savedBodyOverflow = '';
	}
}

const CLONE_FIT_VALUES = ['freeze', 'scale', 'reflow'];
const HANDOFF_VALUES = ['fade', 'hard'];

// A hard handoff's switch point is pinned strictly inside (0, 1): its two sparse
// opacity keys are switch*100 and switch*100 + 0.01, and both must stay clear of
// the 0 and 100 geometry keyframes.
const HARD_SWITCH_MIN = 0.0001;
const HARD_SWITCH_MAX = 0.9998;

// A reflow clone latches to the destination layout once both blob sides are within this
// fraction of the destination's, so fixed-size content can't pop at the switch.
const REFLOW_LATCH_ENTER = 0.02;
// Hysteresis — it only unlatches back to per-frame layout past this, so it can't flap.
const REFLOW_LATCH_EXIT = 0.04;

/**
 * Falls back to a default for an unrecognized enum value, warning once per run so
 * a typo surfaces instead of silently animating with the default choreography.
 * @param {string} key - Option name, for the warning
 * @param {*} value - Resolved value
 * @param {string[]} allowed - Accepted values
 * @param {string} fallback - Value used when `value` is not accepted
 * @returns {string} `value` when accepted, else `fallback`
 */
function coerceEnum(key, value, allowed, fallback) {
	if (allowed.includes(value)) return value;
	const quoted = allowed.map((v) => `"${v}"`);
	const expected =
		quoted.length > 1
			? `${quoted.slice(0, -1).join(', ')} or ${quoted[quoted.length - 1]}`
			: quoted[0];
	console.warn(
		`MorphEngine: unknown ${key} "${value}" — falling back to "${fallback}" (expected ${expected}).`
	);
	return fallback;
}

/** Total border widths from a computed or per-frame styles object; a missing side counts as 0. */
function borderInsets(styles) {
	return {
		x: (parseFloat(styles.borderLeftWidth) || 0) + (parseFloat(styles.borderRightWidth) || 0),
		y: (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0),
	};
}

function clamp(value, min, max) {
	return Math.min(max, Math.max(min, value));
}

function round(value) {
	return Math.round(value * 100) / 100;
}

/**
 * Shared-element morph engine. A fixed-position blob springs from a source
 * element's rect and styles to a target element's, dissolving the source's
 * content on the way out and revealing the target — mirrored to the blob's
 * geometry so it inherits the spring's settle bounce — on the way in.
 *
 * show() morphs source → target; hide() morphs back. Calling either mid-flight
 * reverses the spring in place. Emits: show, hide, change, shown, hidden, stop,
 * complete.
 */
export class MorphEngine extends EventEmitter {
	#spring;
	#attraction;
	#friction;
	#frames = null;
	#blob = null;
	#cloneWrapper = null;
	// per-lifecycle `container` override from show(); the hide leg flies in the same one
	#containerOverride = undefined;
	// viewport → blob-coordinate correction when the container is (or is inside) a
	// containing block for fixed descendants — see #createBlob
	#fixedOffset = { top: 0, left: 0 };

	#styleProperties;

	#state = 'idle';
	#p = 0;
	#resolveRun = null;
	#pendingComplete = null;

	// logical pair for the current show → hide lifecycle
	#sourceElement = null;
	#targetElement = null;
	// a source stop() was asked to leave hidden — see restoreSource()
	#heldSource = null;
	#displayOverride = null;
	#savedInline = new Map();
	#holdsScrollLock = false;

	// current keyframe mapping (roles swap between show and hide)
	#fromMeasure = null;
	#toMeasure = null;
	#toElement = null;
	#shownPosition = TRAVEL; // spring position where the target is fully shown
	#revealed = false;
	#revealStart = 0.75; // p where the target fade-in begins
	#revealFull = 0.875; // p where the target is fully opaque and the blob starts fading
	#sourceRevealed = false;
	#sourceRevealUntil = 0.25; // p where the source reveal window ends (mirrors revealStart at p→0)
	#cloneFadeUntil = 0.25; // per-flight snapshot of the source-content clone fade window
	#cloneFit = 'freeze'; // per-flight snapshot: 'freeze' | 'scale' | 'reflow' clone sizing
	#cloneReflowSettled = false; // reflow: the clone has been laid out once at the destination size
	#handoff = 'fade'; // per-flight snapshot: 'fade' | 'hard' target/blob swap

	// early-settle detector — reset at every animateTo (see #armSettle)
	#springTarget = TRAVEL; // spring position this run is heading toward
	#lastPosition = 0; // spring position at the previous change event
	#settleCount = 0; // consecutive sub-pixel change events

	/**
	 * @param {Object} [options]
	 * @param {number} [options.attraction=0.1] - Spring attraction (0, 1) exclusive
	 * @param {number} [options.friction=0.32] - Spring friction (0, 1) exclusive
	 * @param {string[]} [options.styleProperties] - Computed styles captured and morphed
	 *   (camelCase longhands — shorthands snap instead of interpolating)
	 * @param {number} [options.revealAt=0.75] - Progress where the target reveal window begins
	 * @param {number} [options.sourceRevealUntil=0.25] - Progress where the source reveal window
	 *   ends (mirrors revealAt at the p→0 end so reversals crossfade instead of hard-swapping)
	 * @param {number} [options.cloneFadeUntil=0.25] - Progress where the source-content clone
	 *   finishes dissolving
	 * @param {boolean} [options.cloneContents=true] - Clone the source's content into the blob
	 * @param {'freeze'|'scale'|'reflow'} [options.cloneFit='freeze'] - How the frozen clone is
	 *   sized as the blob resizes. 'freeze' keeps it at the source's pixel size (text never
	 *   rewraps); 'scale' scales it with the blob's border box, for photo-style continuous
	 *   morphs; 'reflow' lays it out at the blob's size every frame — fluid children follow
	 *   the box, fixed-size children (text, padding) keep their size — until the blob is
	 *   within a couple percent of the destination box, then lays it out once at the
	 *   destination size and rides the settle on a near-1 scale
	 * @param {'fade'|'hard'} [options.handoff='fade'] - How the blob hands off to the target.
	 *   'fade' ramps the target in and then fades the blob out; 'hard' swaps both in one
	 *   instant at revealAt (the switch point is clamped strictly inside (0, 1))
	 * @param {Object} [options.hide] - Sparse overrides for the hide leg
	 * @param {number} [options.hide.attraction] - Hide spring attraction
	 * @param {number} [options.hide.friction] - Hide spring friction
	 * @param {number} [options.hide.revealAt] - Hide target reveal start
	 * @param {number} [options.hide.sourceRevealUntil] - Hide source reveal end
	 * @param {number} [options.hide.cloneFadeUntil] - Hide clone fade end
	 * @param {boolean} [options.hide.cloneContents] - Hide clone-content setting
	 * @param {'freeze'|'scale'|'reflow'} [options.hide.cloneFit] - Hide clone sizing mode
	 * @param {'fade'|'hard'} [options.hide.handoff] - Hide handoff mode
	 * @param {boolean} [options.lockScroll=true] - Lock body scroll from show until fully
	 *   hidden — a scroll mid-morph would strand the fixed-position blob
	 * @param {number} [options.zIndex=9999] - Blob z-index — competes with the container's
	 *   other children, not the page's
	 * @param {Element|(() => Element|null)} [options.container=document.body] - Element the
	 *   blob is appended to for each flight, or a function returning one (called per flight).
	 *   A fixed blob in body paints under the browser top layer, so a flight into or out of
	 *   an open showModal() dialog must fly inside that dialog's subtree
	 */
	constructor({
		attraction = 0.1,
		friction = 0.32,
		styleProperties = DEFAULT_STYLE_PROPERTIES,
		revealAt = 0.75,
		sourceRevealUntil = 0.25,
		cloneFadeUntil = 0.25,
		cloneContents = true,
		cloneFit = 'freeze',
		handoff = 'fade',
		hide = {},
		lockScroll = true,
		zIndex = 9999,
		container = null,
	} = {}) {
		super();

		this.#attraction = attraction;
		this.#friction = friction;
		this.#spring = new PhysicsEngine({ attraction, friction });
		this.#styleProperties = styleProperties;

		this.revealAt = revealAt;
		this.sourceRevealUntil = sourceRevealUntil;
		this.cloneFadeUntil = cloneFadeUntil;
		this.cloneContents = cloneContents;
		this.cloneFit = cloneFit;
		this.handoff = handoff;
		this.hideConfig = hide;
		this.lockScroll = lockScroll;
		this.zIndex = zIndex;
		this.container = container;

		this.#spring.on('change', ({ position }) => {
			if (this.#state !== 'showing' && this.#state !== 'hiding') return;
			const p = position / TRAVEL;
			this.#p = p;
			this.#applyFrame(p);
			this.emit('change', { progress: p, phase: this.#state });

			// Early finalize: physics-engine only fires 'complete' once the spring is
			// within 1e-2 of its end — ~0.5s past the point motion is visually done. Snap
			// to the exact end frame and settle as soon as we're sub-pixel from the target
			// for two consecutive frames. The per-frame-delta guard is load-bearing: the
			// first overshoot crossing of the target has a large delta and must NOT qualify,
			// so the settle bounce is preserved.
			if (
				Math.abs(position - this.#springTarget) < SETTLE_POSITION_EPSILON &&
				Math.abs(position - this.#lastPosition) < SETTLE_DELTA_EPSILON
			) {
				if (++this.#settleCount >= 2) {
					this.#applyFrame(this.#springTarget / TRAVEL);
					this.#spring.stop();
					this.#settle();
					return;
				}
			} else {
				this.#settleCount = 0;
			}
			this.#lastPosition = position;
		});
		this.#spring.on('complete', () => this.#settle());
	}

	/** @returns {string} 'idle' | 'showing' | 'shown' | 'hiding' */
	get state() {
		return this.#state;
	}

	/** @returns {number} Last-known morph progress (overshoots past 1 while settling) */
	get progress() {
		return this.#p;
	}

	/**
	 * Morphs from the source element to the target element. Called while hiding,
	 * it reverses the in-flight morph instead (arguments are ignored).
	 * @param {Object} options
	 * @param {HTMLElement} options.from - Source element (stays hidden while shown)
	 * @param {HTMLElement} options.to - Target element (revealed as the blob arrives)
	 * @param {string} [options.display] - display value applied to a display:none target
	 * @param {boolean} [options.oneWay=false] - Complete and hand ownership to the target on settle
	 * @param {number} [options.attraction] - One-off spring attraction
	 * @param {number} [options.friction] - One-off spring friction
	 * @param {number} [options.revealAt] - One-off target reveal start
	 * @param {number} [options.sourceRevealUntil] - One-off source reveal end
	 * @param {number} [options.cloneFadeUntil] - One-off clone fade end
	 * @param {boolean} [options.cloneContents] - One-off clone-content setting
	 * @param {'freeze'|'scale'|'reflow'} [options.cloneFit] - One-off clone sizing mode
	 * @param {'fade'|'hard'} [options.handoff] - One-off handoff mode
	 * @param {Element|(() => Element|null)} [options.container] - Where the blob is appended
	 *   for this show → hide lifecycle; wins over the constructor's `container`
	 * @returns {Promise<boolean>} true when settled, false if superseded or rejected
	 */
	show({
		from,
		to,
		display = null,
		oneWay = false,
		attraction,
		friction,
		revealAt,
		sourceRevealUntil,
		cloneFadeUntil,
		cloneContents,
		cloneFit,
		handoff,
		container,
	} = {}) {
		const overrides = {
			attraction,
			friction,
			revealAt,
			sourceRevealUntil,
			cloneFadeUntil,
			cloneContents,
			cloneFit,
			handoff,
		};
		if (this.#state === 'showing' || this.#state === 'shown') {
			console.warn(`MorphEngine: show() ignored — already ${this.#state}`);
			return Promise.resolve(false);
		}
		if (this.#state === 'hiding') {
			const promise = this.#reverse('showing', overrides);
			if (oneWay) this.#pendingComplete = { restoreSource: false };
			return promise;
		}

		if (!from || !to) throw new Error('MorphEngine: show() requires { from, to } elements.');

		this.#sourceElement = from;
		this.#targetElement = to;
		this.#displayOverride = display;
		this.#containerOverride = container;
		// A source held by stop({ restoreSource: false }) belongs to the previous
		// flight, and its inline styles are the morph's own. Releasing it here is
		// what stops #saveInline from snapshotting those hidden styles as though
		// they were the page's — which would make them permanent.
		this.restoreSource();
		this.#saveInline(from);
		this.#saveInline(to);

		if (this.lockScroll && !this.#holdsScrollLock) {
			this.#holdsScrollLock = true;
			acquireScrollLock();
		}

		const promise = this.#morph(from, to, 'showing', overrides);
		if (oneWay) this.#pendingComplete = { restoreSource: false };
		return promise;
	}

	/**
	 * Morphs back from the target to the source. Called while showing, it
	 * reverses the in-flight morph.
	 * @param {Object} [options]
	 * @param {number} [options.attraction] - One-off spring attraction
	 * @param {number} [options.friction] - One-off spring friction
	 * @param {number} [options.revealAt] - One-off target reveal start
	 * @param {number} [options.sourceRevealUntil] - One-off source reveal end
	 * @param {number} [options.cloneFadeUntil] - One-off clone fade end
	 * @param {boolean} [options.cloneContents] - One-off clone-content setting
	 * @param {'freeze'|'scale'|'reflow'} [options.cloneFit] - One-off clone sizing mode
	 * @param {'fade'|'hard'} [options.handoff] - One-off handoff mode
	 * @returns {Promise<boolean>} true when settled, false if superseded or rejected
	 */
	hide({
		attraction,
		friction,
		revealAt,
		sourceRevealUntil,
		cloneFadeUntil,
		cloneContents,
		cloneFit,
		handoff,
	} = {}) {
		const overrides = {
			attraction,
			friction,
			revealAt,
			sourceRevealUntil,
			cloneFadeUntil,
			cloneContents,
			cloneFit,
			handoff,
		};
		if (this.#state === 'idle' || this.#state === 'hiding') {
			console.warn(`MorphEngine: hide() ignored — ${this.#state}`);
			return Promise.resolve(false);
		}
		if (this.#state === 'showing') return this.#reverse('hiding', overrides);

		// fresh re-measure of both — the page may have scrolled or resized while shown
		return this.#morph(this.#targetElement, this.#sourceElement, 'hiding', overrides);
	}

	/**
	 * Hands a shown or showing one-way morph to the target without emitting stop.
	 * @param {Object} [options]
	 * @param {boolean} [options.restoreSource=false] - Fully restore the source on handoff
	 * @returns {boolean} True when completed immediately or armed for settle.
	 */
	complete({ restoreSource = false } = {}) {
		if (this.#state === 'shown') {
			this.#finalizeComplete(restoreSource);
			return true;
		}
		if (this.#state === 'showing') {
			this.#pendingComplete = { restoreSource };
			return true;
		}

		console.warn(`MorphEngine: complete() ignored — ${this.#state}`);
		return false;
	}

	/**
	 * Aborts any morph and restores both elements to their pre-show resting state.
	 *
	 * `restoreSource: false` makes this a transport HANDOFF rather than an abort:
	 * the blob goes, the target is restored and scroll unlocks, but the source
	 * stays hidden and keeps its `morphing` mark, because a morph does still own
	 * it. The caller is taking the flight over and calls `restoreSource()` when it
	 * is genuinely finished. Without the option a caller that only wants the blob
	 * gone has to re-hide the source itself in the same synchronous task, or the
	 * source flashes at full opacity for a frame — a timing invariant nothing can
	 * enforce from the outside.
	 * @param {Object} [options]
	 * @param {boolean} [options.restoreSource=true] - Restore the source now.
	 */
	stop({ restoreSource = true } = {}) {
		this.#pendingComplete = null;
		if (this.#state === 'idle') return;

		this.#supersede();
		this.#spring.stop();
		this.#removeBlob();

		const source = this.#sourceElement;
		const target = this.#targetElement;
		if (source) {
			if (restoreSource) {
				this.#restoreInline(source);
				source.removeAttribute('morphing');
			} else {
				this.#heldSource = source;
			}
		}
		if (target) {
			this.#restoreInline(target);
			target.removeAttribute('morphing');
			target.removeAttribute('morph-shown');
		}
		this.#unlockScroll();

		const progress = this.#p;
		this.#state = 'idle';
		this.#p = 0;
		this.emit('stop', { progress });
	}

	/**
	 * Restores a source element held back by `stop({ restoreSource: false })`.
	 *
	 * Idempotent, and harmless on a detached element. `show()` and `destroy()`
	 * call it so a held source can never leak into the next flight — one engine
	 * is routinely reused run after run.
	 * @returns {boolean} True when a held source was restored.
	 */
	restoreSource() {
		const source = this.#heldSource;
		if (!source) return false;
		this.#heldSource = null;
		this.#restoreInline(source);
		source.removeAttribute('morphing');
		return true;
	}

	/**
	 * Stops and removes all listeners. The engine is unusable afterwards.
	 */
	destroy() {
		this.stop();
		// stop() early-returns when the engine is already idle, which is exactly
		// the state a held source lives in.
		this.restoreSource();
		this.#spring.removeAllListeners();
		this.removeAllListeners();
	}

	/** @param {number} attraction - Show/default attraction, applied live to the spring */
	setAttraction(attraction) {
		this.#attraction = attraction;
		this.#spring.setAttraction(attraction);
	}

	/** @param {number} friction - Show/default friction, applied live to the spring */
	setFriction(friction) {
		this.#friction = friction;
		this.#spring.setFriction(friction);
	}

	// -- Morph lifecycle --

	/**
	 * The single morph routine — show and hide are the same mechanics with the
	 * roles swapped. The blob starts pixel-identical to fromElement (its content
	 * cloned and frozen on top), springs to toElement's rect and styles, and
	 * reveals toElement across the final stretch.
	 */
	#morph(fromElement, toElement, phase, overrides = {}) {
		this.#pendingComplete = null;
		this.#supersede();
		const config = this.#resolveConfig(phase, overrides);
		this.#spring.setAttraction(config.attraction);
		this.#spring.setFriction(config.friction);

		const fromMeasure = this.#measure(fromElement);
		const toMeasure = this.#measure(toElement);

		this.#fromMeasure = fromMeasure;
		this.#toMeasure = toMeasure;
		this.#toElement = toElement;
		this.#shownPosition = phase === 'showing' ? TRAVEL : 0;
		this.#state = phase;
		this.#revealed = false;
		this.#sourceRevealed = false;

		// target fades in across [revealStart, revealFull]; blob fades out across
		// [revealFull, 1] — staggered so the surface never dips translucent mid-swap
		this.#revealStart = config.revealAt;
		this.#revealFull = config.revealAt + (1 - config.revealAt) / 2;
		this.#sourceRevealUntil = config.sourceRevealUntil;
		this.#cloneFadeUntil = config.cloneFadeUntil;
		this.#cloneFit = config.cloneFit;
		this.#handoff = config.handoff;
		// A hard handoff collapses both ramps into one instant at revealAt. The switch
		// point is clamped strictly inside (0, 1) so both of its sparse opacity keys land
		// inside (0, 100) and can never collide with the 0/100 geometry keyframes — at
		// revealAt 0 the step would otherwise land on key 0, and at 1 on key 100.
		if (config.handoff === 'hard') {
			this.#revealFull = clamp(config.revealAt, HARD_SWITCH_MIN, HARD_SWITCH_MAX);
		}

		this.#reconcileBorderColors(fromMeasure, toMeasure);
		this.#frames = new FrameEngine(this.#buildKeyframes(fromMeasure, toMeasure));

		this.#removeBlob();
		// resolved per flight — a function form answers for the pair in hand (e.g. the
		// source's nearest open modal dialog), and the hide leg lands in the same place
		const container = resolveBlobContainer(this.#containerOverride, this.container, document.body);
		this.#createBlob(fromMeasure, toMeasure, config.cloneContents, container);
		this.#markElements(phase);

		// transitions on the real elements would fight the per-frame writes
		fromElement.style.transition = 'none';
		toElement.style.transition = 'none';

		// the blob now covers fromElement pixel-for-pixel — swap the real one out
		fromElement.style.visibility = 'hidden';
		toElement.style.visibility = 'hidden';
		toElement.style.opacity = '0';

		this.#applyFrame(0);
		this.emit(phase === 'showing' ? 'show' : 'hide', {
			from: this.#sourceElement,
			to: this.#targetElement,
		});

		const promise = new Promise((resolve) => {
			this.#resolveRun = resolve;
		});
		this.#armSettle(0, TRAVEL);
		this.#spring.animateTo(0, TRAVEL);
		return promise;
	}

	/**
	 * Reverses the in-flight morph. The keyframe mapping, blob, and clone are all
	 * pure functions of p, so travelling back unwinds everything automatically —
	 * the reveal window un-reveals, the clone fades back in, and the blob lands
	 * exactly where it started.
	 */
	#reverse(newPhase, overrides = {}) {
		this.#pendingComplete = null;
		this.#supersede();
		const config = this.#resolveConfig(newPhase, overrides);
		this.#spring.setAttraction(config.attraction);
		this.#spring.setFriction(config.friction);
		this.#state = newPhase;
		this.#markElements(newPhase);

		const targetPosition =
			newPhase === 'showing' ? this.#shownPosition : TRAVEL - this.#shownPosition;

		this.emit(newPhase === 'showing' ? 'show' : 'hide', {
			from: this.#sourceElement,
			to: this.#targetElement,
		});

		const promise = new Promise((resolve) => {
			this.#resolveRun = resolve;
		});
		this.#armSettle(this.#p * TRAVEL, targetPosition);
		this.#spring.animateTo(this.#p * TRAVEL, targetPosition);
		return promise;
	}

	/**
	 * Arms the early-settle detector for a fresh run. Called at every animateTo so
	 * a reversal never inherits stale proximity state from the run it interrupts.
	 * @param {number} startPosition - Spring position the run begins from
	 * @param {number} target - Spring position the run is heading toward
	 */
	#armSettle(startPosition, target) {
		this.#springTarget = target;
		this.#lastPosition = startPosition;
		this.#settleCount = 0;
	}

	/**
	 * Spring settled — finalize whichever logical state we were heading toward.
	 * The spring's final change event already applied the exact end frame.
	 */
	#settle() {
		if (this.#state !== 'showing' && this.#state !== 'hiding') return;

		const resolve = this.#resolveRun;
		this.#resolveRun = null;

		if (this.#state === 'showing') this.#finalizeShown();
		else this.#finalizeHidden();

		if (this.#state === 'shown' && this.#pendingComplete) {
			const { restoreSource } = this.#pendingComplete;
			this.#pendingComplete = null;
			this.#finalizeComplete(restoreSource);
		}

		if (resolve) resolve(true);
	}

	#finalizeShown() {
		this.#removeBlob();

		const source = this.#sourceElement;
		const target = this.#targetElement;

		// the target rests naturally — clear the mirror transform, keep it visible
		this.#restoreProperties(target, [
			'opacity',
			'transform',
			'transformOrigin',
			'willChange',
			'transition',
		]);
		target.style.visibility = 'visible';

		// clear any mirror residue off the source: from the source-reveal window at the
		// start of this show, or from the target machinery if we got here by reversing a
		// hide (then #toElement === source). Always runs — a fresh show leaves transform/
		// willChange on the source that #ensureSourceUnrevealed does not touch.
		this.#restoreProperties(source, ['opacity', 'transform', 'transformOrigin', 'willChange']);
		// the source stays hidden while shown — it "became" the target
		source.style.visibility = 'hidden';

		source.removeAttribute('morphing');
		target.removeAttribute('morphing');
		target.setAttribute('morph-shown', '');

		this.#state = 'shown';
		this.emit('shown', { from: source, to: target });
	}

	#finalizeHidden() {
		this.#removeBlob();

		const source = this.#sourceElement;
		const target = this.#targetElement;

		this.#restoreInline(source);
		this.#restoreInline(target);
		source.removeAttribute('morphing');
		target.removeAttribute('morphing');
		target.removeAttribute('morph-shown');
		this.#unlockScroll();

		this.#state = 'idle';
		this.#p = 0;
		this.emit('hidden', { from: source, to: target });
	}

	/**
	 * Releases a shown morph to app ownership while preserving the target's state.
	 * @param {boolean} restoreSource - Whether to fully restore the source element
	 */
	#finalizeComplete(restoreSource) {
		const source = this.#sourceElement;
		const target = this.#targetElement;

		this.#savedInline.delete(target);
		target.removeAttribute('morph-shown');

		if (restoreSource) this.#restoreInline(source);
		else {
			this.#restoreProperties(source, ['transition']);
			this.#savedInline.delete(source);
		}

		this.#unlockScroll();
		this.#sourceElement = null;
		this.#targetElement = null;
		this.#displayOverride = null;
		this.#state = 'idle';
		this.#p = 0;
		this.emit('complete', { from: source, to: target });
	}

	/** Releases this engine's share of the module-level body scroll lock. */
	#unlockScroll() {
		if (!this.#holdsScrollLock) return;
		this.#holdsScrollLock = false;
		releaseScrollLock();
	}

	/**
	 * Resolves sparse public, hide-leg, and per-call settings for a run.
	 * @param {string} phase - 'showing' or 'hiding'
	 * @param {Object} overrides - Sparse per-call overrides
	 * @returns {Object} Resolved spring and choreography settings
	 */
	#resolveConfig(phase, overrides = {}) {
		const config = {
			attraction: this.#attraction,
			friction: this.#friction,
			revealAt: this.revealAt,
			sourceRevealUntil: this.sourceRevealUntil,
			cloneFadeUntil: this.cloneFadeUntil,
			cloneContents: this.cloneContents,
			cloneFit: this.cloneFit,
			handoff: this.handoff,
		};
		const keys = Object.keys(config);
		if (phase === 'hiding') {
			for (const key of keys) {
				if (this.hideConfig[key] !== undefined) config[key] = this.hideConfig[key];
			}
		}
		for (const key of keys) {
			if (overrides[key] !== undefined) config[key] = overrides[key];
		}
		config.cloneFit = coerceEnum('cloneFit', config.cloneFit, CLONE_FIT_VALUES, 'freeze');
		config.handoff = coerceEnum('handoff', config.handoff, HANDOFF_VALUES, 'fade');
		return config;
	}

	/** Resolves a superseded run's promise with false. */
	#supersede() {
		if (this.#resolveRun) {
			this.#resolveRun(false);
			this.#resolveRun = null;
		}
	}

	// -- Per-frame pipeline --

	/**
	 * The whole visual state as a pure function of p. Reveal handling is an
	 * idempotent check rather than a one-shot flag so a reversed spring that
	 * swings p back down automatically un-reveals the target.
	 */
	#applyFrame(p) {
		const styles = this.#frames.getFrame(p);

		for (const property of CLAMP_POSITIVE) {
			if (property in styles && parseFloat(styles[property]) < 0) styles[property] = '0px';
		}

		Object.assign(this.#blob.style, styles);
		// `styles.top/left` stay in viewport space for the mirror transforms below; only
		// the blob's own writes are shifted into its containing block's space
		const offset = this.#fixedOffset;
		if (offset.top !== 0) this.#blob.style.top = `${parseFloat(styles.top) - offset.top}px`;
		if (offset.left !== 0) this.#blob.style.left = `${parseFloat(styles.left) - offset.left}px`;
		this.#blob.style.boxShadow = lerpShadow(this.#fromMeasure.shadow, this.#toMeasure.shadow, p);

		if (this.#cloneWrapper) {
			const fade =
				this.#cloneFadeUntil > 0 ? clamp(1 - p / this.#cloneFadeUntil, 0, 1) : p <= 0 ? 1 : 0;
			this.#cloneWrapper.style.opacity = String(fade);

			// 'reflow' lays the clone out at the blob's size each frame, so what is
			// fluid inside it follows the box and what is fixed (text, padding) keeps
			// its size — the same layout the destination will have. Once the blob is
			// within a couple percent of the destination box it latches: laid out once
			// at the destination size and only transformed from there, so the settle
			// (overshoot included) never triggers layout and nothing fixed-size pops at
			// the switch. It unlatches back to per-frame layout only past the wider exit
			// tolerance, so a reversal returns cleanly without flapping at the boundary.
			// Skipped entirely while the clone is invisible; re-entry rewrites the box.
			if (this.#cloneFit === 'reflow' && fade > 0) {
				const wrapper = this.#cloneWrapper;
				// the wrapper sits in the blob's padding box, so both boxes are border
				// box minus border widths
				const frameInsets = borderInsets(styles);
				const blobWidth = Math.max(0, parseFloat(styles.width) - frameInsets.x);
				const blobHeight = Math.max(0, parseFloat(styles.height) - frameInsets.y);
				const toInsets = this.#toMeasure.borderInsets;
				const toWidth = this.#toMeasure.rect.width - toInsets.x;
				const toHeight = this.#toMeasure.rect.height - toInsets.y;

				let latched = false;
				if (toWidth > 0 && toHeight > 0) {
					const offWidth = Math.abs(blobWidth / toWidth - 1);
					const offHeight = Math.abs(blobHeight / toHeight - 1);
					const tolerance = this.#cloneReflowSettled ? REFLOW_LATCH_EXIT : REFLOW_LATCH_ENTER;
					latched = offWidth < tolerance && offHeight < tolerance;
				}

				if (latched) {
					if (!this.#cloneReflowSettled) {
						this.#cloneReflowSettled = true;
						wrapper.style.width = `${toWidth}px`;
						wrapper.style.height = `${toHeight}px`;
					}
					wrapper.style.transform = `scale(${blobWidth / toWidth}, ${blobHeight / toHeight})`;
				} else {
					if (this.#cloneReflowSettled) {
						this.#cloneReflowSettled = false;
						wrapper.style.transform = '';
					}
					wrapper.style.width = `${blobWidth}px`;
					wrapper.style.height = `${blobHeight}px`;
				}
			}

			// 'scale' rides the blob's border box instead of staying frozen at the
			// source's pixel size — a photo keeps growing instead of dissolving
			else if (this.#cloneFit === 'scale') {
				const fromRect = this.#fromMeasure.rect;
				if (fromRect.width > 0 && fromRect.height > 0) {
					const scaleX = parseFloat(styles.width) / fromRect.width;
					const scaleY = parseFloat(styles.height) / fromRect.height;
					this.#cloneWrapper.style.transform = `scale(${scaleX}, ${scaleY})`;
				}
			}
		}

		if (p >= this.#revealStart) {
			this.#ensureRevealed();

			const target = this.#toElement;
			const naturalRect = this.#toMeasure.rect;
			const blobRect = {
				top: parseFloat(styles.top),
				left: parseFloat(styles.left),
				width: parseFloat(styles.width),
				height: parseFloat(styles.height),
			};

			// mirror the blob's geometry so the target moves in lockstep and inherits
			// the spring's overshoot bounce once the blob has faded away
			// a hard handoff has a zero-width ramp (revealFull === revealStart)
			const rampWidth = this.#revealFull - this.#revealStart;
			const fadeProgress = rampWidth > 0 ? clamp((p - this.#revealStart) / rampWidth, 0, 1) : 1;
			target.style.opacity = String(fadeProgress);
			target.style.transformOrigin = '0 0';
			target.style.transform =
				`translate(${round(blobRect.left - naturalRect.left)}px, ` +
				`${round(blobRect.top - naturalRect.top)}px) ` +
				`scale(${blobRect.width / naturalRect.width}, ${blobRect.height / naturalRect.height})`;
		} else {
			this.#ensureUnrevealed();
		}

		// Mirror of the target reveal at the p→0 end. The real from-element fades in
		// under the blob over [0, sourceRevealUntil] while the blob fades to nothing, so a
		// morph reversed back to closed crossfades to the live source instead of hard-
		// swapping a stale full-opacity replica. Runs at morph start too — there the blob
		// simply fades in over the pixel-identical source. Direction-agnostic by design.
		if (p <= this.#sourceRevealUntil) {
			this.#ensureSourceRevealed();

			const source = this.#fromMeasure.element;
			const naturalRect = this.#fromMeasure.rect;
			const blobRect = {
				top: parseFloat(styles.top),
				left: parseFloat(styles.left),
				width: parseFloat(styles.width),
				height: parseFloat(styles.height),
			};

			// source fades in over the outer half of the window (descending p) so the
			// composite is opaque before the blob vanishes; mirror the blob's geometry so
			// the source rides its overshoot bounce, exactly like the target
			const half = this.#sourceRevealUntil / 2;
			const sourceOpacity = clamp((this.#sourceRevealUntil - p) / half, 0, 1);
			source.style.opacity = String(sourceOpacity);
			source.style.transformOrigin = '0 0';
			source.style.transform =
				`translate(${round(blobRect.left - naturalRect.left)}px, ` +
				`${round(blobRect.top - naturalRect.top)}px) ` +
				`scale(${blobRect.width / naturalRect.width}, ${blobRect.height / naturalRect.height})`;

			// blob fades out over [w/2, w/4] and is gone below w/4 — clearing before the
			// spring's slow tail so it never sits as a translucent slab muting the live
			// source's content (the source is fully opaque from w/2 down, so the composite
			// never dips). Post-multiply after the keyframe opacity above.
			const quarter = half / 2;
			const blobFactor = clamp((p - quarter) / quarter, 0, 1);
			this.#blob.style.opacity = String(parseFloat(styles.opacity ?? '1') * blobFactor);
		} else {
			this.#ensureSourceUnrevealed();
		}
	}

	#ensureRevealed() {
		if (this.#revealed) return;
		this.#revealed = true;

		const target = this.#toElement;
		if (this.#toMeasure.wasDisplayNone) target.style.display = this.#displayOverride || 'block';
		target.style.visibility = 'visible';
		target.style.willChange = 'transform, opacity';

		// the run's destination just started painting — still at opacity 0, which
		// makes this the seam-free moment for consumers to do layer promotion
		// (e.g. dialog.showModal(): promoting at settle repaints a visible surface)
		this.emit('reveal', { from: this.#fromMeasure.element, to: target });
	}

	#ensureUnrevealed() {
		if (!this.#revealed) return;
		this.#revealed = false;

		const target = this.#toElement;
		target.style.visibility = 'hidden';
		target.style.opacity = '0';

		this.emit('unreveal', { from: this.#fromMeasure.element, to: target });
	}

	/**
	 * Source mirror of #ensureRevealed — makes the real from-element paintable so it
	 * can crossfade in under the blob at the p→0 end. Idempotent.
	 */
	#ensureSourceRevealed() {
		if (this.#sourceRevealed) return;
		this.#sourceRevealed = true;

		const source = this.#fromMeasure.element;
		if (this.#fromMeasure.wasDisplayNone) source.style.display = this.#displayOverride || 'block';
		source.style.visibility = 'visible';
		source.style.willChange = 'transform, opacity';
	}

	/**
	 * Source mirror of #ensureUnrevealed — re-hides the from-element once p leaves the
	 * source window. Transform/willChange residue is cleared at finalize. Idempotent.
	 */
	#ensureSourceUnrevealed() {
		if (!this.#sourceRevealed) return;
		this.#sourceRevealed = false;

		const source = this.#fromMeasure.element;
		source.style.visibility = 'hidden';
		source.style.opacity = '0';
	}

	// -- Measurement & DOM --

	/**
	 * Measures an element's viewport rect and captured computed styles. A
	 * display:none element is flipped on invisibly for one synchronous read.
	 * (visibility:hidden elements keep their layout and measure normally.)
	 */
	#measure(element) {
		let restore = null;
		if (element.getClientRects().length === 0) {
			const style = element.style;
			restore = {
				display: style.display,
				visibility: style.visibility,
				transition: style.transition,
			};
			style.transition = 'none';
			style.visibility = 'hidden';
			style.display = this.#displayOverride || 'block';
		}

		const rect = element.getBoundingClientRect();
		const computed = getComputedStyle(element);
		const styles = {};
		for (const property of this.#styleProperties) {
			const value = computed[property];
			// Colors are normalized to rgba() here, at the only point they enter the
			// pipeline. Chrome serializes a Tailwind opacity modifier
			// (color-mix(in oklab, …)) as `oklab(L a b / a)`, which frame-engine does
			// not recognize as a color: pair one with a legacy rgb() at the other end
			// and it interpolates to `rgb(NaN,NaN,NaN)`, an invalid value the CSSOM
			// drops — the blob's border then falls back to currentColor and paints an
			// opaque line for the whole flight.
			styles[property] = /color$/i.test(property) ? (normalizeColor(value) ?? value) : value;
		}
		const measure = {
			element,
			rect,
			styles,
			shadow: parseShadow(computed.boxShadow),
			borderStyle: computed.borderTopStyle,
			// read here rather than from styles, which only has them when styleProperties does
			borderInsets: borderInsets(computed),
			// applied statically to the blob (see #createBlob) — never added to
			// styleProperties, as a blur radius or image URL has no meaningful midpoint
			backdropFilter: computed.backdropFilter || computed.webkitBackdropFilter,
			backgroundImage: computed.backgroundImage,
			backgroundSize: computed.backgroundSize,
			backgroundRepeat: computed.backgroundRepeat,
			backgroundPosition: computed.backgroundPosition,
			wasDisplayNone: restore !== null,
		};

		if (restore) Object.assign(element.style, restore);
		return measure;
	}

	/**
	 * A borderless element's computed border-color falls back to currentColor (its
	 * text color), and `transparent` computes to rgba(0,0,0,0) — lerping toward
	 * either drags the visible end's border through an unrelated hue while the
	 * width or alpha collapses. Rewrite the degenerate end's color so only
	 * width/alpha animate: an absent border holds the visible end's color
	 * verbatim, a fully transparent one holds its hue at alpha 0.
	 */
	#reconcileBorderColors(fromMeasure, toMeasure) {
		const absent = (measure) =>
			measure.borderStyle === 'none' ||
			BORDER_SIDES.every((side) => parseFloat(measure.styles[`border${side}Width`]) === 0);

		const fromAbsent = absent(fromMeasure);
		const toAbsent = absent(toMeasure);
		if (fromAbsent && toAbsent) return;

		for (const side of BORDER_SIDES) {
			const key = `border${side}Color`;
			const fromColor = fromMeasure.styles[key];
			const toColor = toMeasure.styles[key];
			if (!fromColor || !toColor) continue;

			const fromDegenerate = fromAbsent || parseColor(fromColor).alpha === 0;
			const toDegenerate = toAbsent || parseColor(toColor).alpha === 0;
			if (fromDegenerate === toDegenerate) continue;

			const visibleColor = fromDegenerate ? toColor : fromColor;
			const { red, green, blue } = parseColor(visibleColor);
			const replacement = (fromDegenerate ? fromAbsent : toAbsent)
				? visibleColor
				: `rgba(${red}, ${green}, ${blue}, 0)`;
			(fromDegenerate ? fromMeasure : toMeasure).styles[key] = replacement;
		}
	}

	#buildKeyframes(fromMeasure, toMeasure) {
		const rectStyles = (rect) => ({
			top: `${rect.top}px`,
			left: `${rect.left}px`,
			width: `${rect.width}px`,
			height: `${rect.height}px`,
		});

		// blob opacity clears at the midpoint between revealFull and 1, not at 1 —
		// past revealFull the target is fully opaque and mirrored to the blob's rect,
		// so the blob is a featureless slab muting the target's content; fading it on
		// the spring's slow tail makes content contrast "pop in" at the very end.
		// The fade still never starts before the target is fully revealed.
		// A hard handoff replaces that fade with a 0.01%-wide step at revealFull, so the
		// blob is opaque right up to the switch and gone immediately after. Both shapes
		// rely on frame-engine's per-property extrapolation beyond the pair, and on its
		// [0, 1] opacity clamp holding the ends flat (including during overshoot).
		const blobClear =
			this.#handoff === 'hard'
				? this.#revealFull + 0.0001
				: this.#revealFull + (1 - this.#revealFull) / 2;

		// Merge the sparse opacity keys in rather than listing them between the 0/100
		// literals: a key that coincides with an end keyframe (revealAt at the very edge
		// of a fade-mode window) must gain opacity, never replace that end's geometry.
		const frames = {
			0: { ...rectStyles(fromMeasure.rect), ...fromMeasure.styles },
			100: { ...rectStyles(toMeasure.rect), ...toMeasure.styles },
		};
		const setOpacity = (percent, value) => {
			frames[percent] = { ...frames[percent], opacity: value };
		};
		setOpacity(this.#revealFull * 100, '1');
		setOpacity(blobClear * 100, '0');
		return frames;
	}

	#createBlob(fromMeasure, toMeasure, cloneContents, container) {
		const blob = document.createElement('morph-blob');
		Object.assign(blob.style, blobBaseStyle(fromMeasure, toMeasure, this.zIndex));

		// Glass/texture surfaces (backdrop blur, gradient/image fills) have no
		// meaningful midpoint, so they ride statically through the flight rather than
		// interpolating — target's value wins when set, else source's, else omitted.
		const backdropFilter =
			toMeasure.backdropFilter !== 'none'
				? toMeasure.backdropFilter
				: fromMeasure.backdropFilter !== 'none'
					? fromMeasure.backdropFilter
					: null;
		if (backdropFilter) {
			blob.style.backdropFilter = backdropFilter;
			blob.style.webkitBackdropFilter = backdropFilter; // Safari
		}

		// size/repeat/position must come from the same measure as the image itself
		const backgroundMeasure =
			toMeasure.backgroundImage !== 'none'
				? toMeasure
				: fromMeasure.backgroundImage !== 'none'
					? fromMeasure
					: null;
		if (backgroundMeasure) {
			blob.style.backgroundImage = backgroundMeasure.backgroundImage;
			blob.style.backgroundSize = backgroundMeasure.backgroundSize;
			blob.style.backgroundRepeat = backgroundMeasure.backgroundRepeat;
			blob.style.backgroundPosition = backgroundMeasure.backgroundPosition;
		}

		if (cloneContents) this.#createClone(blob, fromMeasure);

		container.appendChild(blob);
		this.#blob = blob;

		// The keyframes are viewport rects and the blob is position: fixed, which only
		// agree while the blob's containing block is the viewport. A container that is —
		// or sits inside — an element with `transform`, `filter`, `backdrop-filter`,
		// `perspective`, `contain: paint | layout`, `will-change: transform`, … becomes
		// the containing block for its fixed descendants, and the blob's top/left are
		// then measured from that box's padding edge instead. The blob is at top/left 0
		// right now, so where it actually landed IS that offset, whatever caused it:
		// one layout read per flight, in the same pass as the two element measures,
		// and it stays correct as browsers add properties to that list (a computed-
		// style walk would need re-auditing each time). Only a translation can be
		// undone this way — a scaled or rotated containing block scales or rotates the
		// blob with it, which is documented as a consumer requirement. Measured once
		// per flight: a flight's geometry is a snapshot of measure time anyway, and the
		// mirror transforms on the real elements stay in viewport space untouched.
		const probe = blob.getBoundingClientRect();
		this.#fixedOffset = { top: probe.top || 0, left: probe.left || 0 };
	}

	/**
	 * Freezes a visual copy of the source's content inside the blob. The wrapper
	 * keeps the source's original dimensions so text never rewraps as the blob
	 * resizes; the blob's overflow:hidden clips it. The clone's own surface
	 * (background, border, shadow) is stripped — the blob renders the surface.
	 * With cloneFit: 'scale' the wrapper keeps those dimensions but is scaled to
	 * the blob's border box every frame from its top-left origin instead. With
	 * cloneFit: 'reflow' the wrapper is resized to the blob's padding box every
	 * frame (see #applyFrame), so the clone lays itself out at each size.
	 */
	#createClone(blob, fromMeasure) {
		const clone = fromMeasure.element.cloneNode(true);
		clone.removeAttribute('id');
		clone.removeAttribute('morphing');
		Object.assign(clone.style, {
			position: 'static',
			margin: '0',
			width: '100%',
			height: '100%',
			transform: 'none',
			transition: 'none',
			visibility: 'visible',
			opacity: '1',
			boxShadow: 'none',
			background: 'transparent',
			borderColor: 'transparent',
		});

		const transforms = this.#cloneFit === 'scale' || this.#cloneFit === 'reflow';
		const wrapper = document.createElement('div');
		Object.assign(wrapper.style, {
			position: 'absolute',
			top: '0',
			left: '0',
			width: `${fromMeasure.rect.width}px`,
			height: `${fromMeasure.rect.height}px`,
			pointerEvents: 'none',
			transformOrigin: '0 0',
			...(transforms ? { willChange: 'transform' } : null),
		});

		wrapper.appendChild(clone);
		blob.appendChild(wrapper);
		this.#cloneWrapper = wrapper;
		this.#cloneReflowSettled = false;
	}

	#removeBlob() {
		if (!this.#blob) return;
		this.#blob.remove();
		this.#blob = null;
		this.#cloneWrapper = null;
		this.#cloneReflowSettled = false;
		this.#fixedOffset = { top: 0, left: 0 };
	}

	/** Marks both elements for CSS hooks — which one the blob is flying away from. */
	#markElements(phase) {
		const showing = phase === 'showing';
		this.#sourceElement.setAttribute('morphing', showing ? 'source' : 'target');
		this.#targetElement.setAttribute('morphing', showing ? 'target' : 'source');
	}

	// -- Inline style bookkeeping --

	#saveInline(element) {
		const saved = {};
		for (const property of MANAGED_PROPERTIES) {
			saved[property] = element.style[property];
		}
		this.#savedInline.set(element, saved);
	}

	#restoreProperties(element, properties) {
		const saved = this.#savedInline.get(element) || {};
		const hasTransition = properties.includes('transition');
		for (const property of properties) {
			if (property === 'transition') continue;
			element.style[property] = saved[property] ?? '';
		}
		if (hasTransition) {
			// commit the restored values while the inline `transition: none` from
			// #morph still wins — restoring everything in one recalc would let a
			// stylesheet `transition: all` animate the visibility flip (and leave
			// it frozen mid-transition in a hidden tab)
			void element.offsetWidth;
			element.style.transition = saved.transition ?? '';
		}
	}

	#restoreInline(element) {
		this.#restoreProperties(element, MANAGED_PROPERTIES);
		this.#savedInline.delete(element);
	}
}
